import { acquireTokenForApi } from "@/authConfig";
import React, { useState } from "react";
import { OverlayTrigger, Popover } from "react-bootstrap";
import { FaUserMinus } from "react-icons/fa";

import ConfirmModal from "@/components/ConfirmModal";
import ErrorModal from "@/components/ErrorModal";

interface GroupRowProps {
  group: any;
  onPlannerClick: (plans: { title: string; id: string }[]) => void;
}

const GroupRow: React.FC<GroupRowProps> = ({ group }) => {
  const [showConfirm, setShowConfirm] = useState(false);
  const [errorModal, setErrorModal] = useState({
    show: false,
    title: "",
    message: "",
  });

  const showError = (title: string, message: string) => {
    setErrorModal({ show: true, title, message });
  };

  const [removing, setRemoving] = useState(false);

  const handleLeaveGroup = async () => {
    try {
      setRemoving(true);
      const token = await acquireTokenForApi();
      const res = await fetch(
        `${import.meta.env.VITE_API_URL}/api/group/${group.id}/members/me`,
        { method: "DELETE", headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error(await res.text());
      document.getElementById(group.id)?.remove();
      setShowConfirm(false);
    } catch (err) {
      console.error(err);
      showError("Failed to Leave Group", "We couldn't remove you from this group. Please try again later or check your permissions.");
    } finally {
      setRemoving(false);
    }
  };

  // Use cached metadata from backend response
  const hasTeam = group.hasTeam || false;
  const hasSite = group.hasSite || false;
  const siteUrl = group.siteUrl || null;
  const plannerPlans = group.plannerPlans || [];

  const plannerPopover = (
    <Popover id={`planner-popover-${group.id}`}>
      <Popover.Body>
        {plannerPlans.map((plan: any) => (
          <div
            key={plan.id}
            className="planner-item cursor-pointer"
            onClick={() =>
              window.open(`https://planner.cloud.microsoft/webui/plan/${plan.id}`, "_blank")
            }
          >
            {plan.title}
          </div>
        ))}
      </Popover.Body>
    </Popover>
  );

  return (
    <div className="row m365-row mygroups-row">
      <div className="col-sm-8">
        {group.isOwner ? (
          <a href={`/group/${group.id}`} className="group-link">
            {group.displayName}
          </a>
        ) : (
          <span>{group.displayName}</span>
        )}
        <span className="ms-1 text-muted">({group.isOwner ? "Owner" : "Member"})</span>
      </div>

      <div className="col-sm-4 text-right">
        {/* Teams */}
        <img
          src={hasTeam ? "/images/microsoft-teams.png" : "/images/microsoft-teams-gray.png"}
          alt="Teams"
          title="Open Team"
          className={`pad-5-left ${hasTeam ? "cursor-pointer" : "disabled"}`}
          onClick={() =>
            hasTeam && window.open(`https://teams.microsoft.com/l/team/${group.id}`, "_blank")
          }
        />

        {/* SharePoint */}
        <img
          src={hasSite ? "/images/microsoft-sharepoint.png" : "/images/microsoft-sharepoint-gray.png"}
          alt="SharePoint"
          title="Open site"
          className={`pad-5-left ${hasSite ? "cursor-pointer" : "disabled"}`}
          onClick={() => hasSite && siteUrl && window.open(siteUrl, "_blank")}
        />

        {/* Planner */}
        <OverlayTrigger
          trigger="click"
          placement="left"
          overlay={plannerPopover}
          rootClose
        >
          <img
            src={plannerPlans.length ? "/images/microsoft-planner.png" : "/images/microsoft-planner-gray.png"}
            alt="Planner"
            title="Open Planner"
            className={`pad-5-left ${plannerPlans.length ? "cursor-pointer" : "disabled"}`}
          />
        </OverlayTrigger>

        <FaUserMinus
          className={`text-danger ${removing ? "disabled" : "cursor-pointer"}`}
          style={{ fontSize: "20px", marginLeft: "5px" }}
          onClick={() => !removing && setShowConfirm(true)}
          title={removing ? "Leaving group..." : "Leave group"}
        />
        <ConfirmModal
          show={showConfirm}
          title="Leave group?"
          message={`You will be removed from this group:\n${group.displayName}`}
          onConfirm={handleLeaveGroup}
          onCancel={() => setShowConfirm(false)}
        />
        <ErrorModal
          show={errorModal.show}
          title={errorModal.title}
          message={errorModal.message}
          onClose={() => setErrorModal({ ...errorModal, show: false })}
        />
      </div>
    </div>
  );
};

export default GroupRow;